// Config → Account Manager Sync. Human-gated harmonise: fill customers that
// have NO account manager from the AM on their most recent quote. Scan →
// review → select → Apply. Nothing is written until Apply is pressed.
import { useMemo, useState } from "react";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { toast } from "sonner";
import { UserCog, Loader2, Search, Check, Crown, Sparkles, ChevronDown } from "lucide-react";
import { useAccountManagers } from "../../hooks/useAccountManagers";

const TIERS = ["BLACK", "GOLD", "SILVER", "BRONZE"];

function money(n) {
  const v = Number(n);
  if (!v) return "£0";
  return "£" + v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(+d) ? "—" : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
}

export default function AccountManagerSync() {
  const managers = useAccountManagers();
  const amNames = useMemo(() => {
    const seen = new Set(); const out = [];
    for (const m of managers) { const n = m.name; if (n && !seen.has(n)) { seen.add(n); out.push(n); } }
    return out.sort();
  }, [managers]);

  const [tier, setTier] = useState("");
  const [limit, setLimit] = useState(50);
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [rows, setRows] = useState(null);           // null = not scanned yet
  const [selected, setSelected] = useState(() => new Set());
  const [overrides, setOverrides] = useState({});   // person_id -> am name override

  const scan = async () => {
    setScanning(true); setSelected(new Set()); setOverrides({});
    try {
      const { data } = await api.get(`/crm/am-sync/preview?tier=${tier}&limit=${limit}`);
      setRows(data.items || []);
      if (!(data.items || []).length) toast.info("No customers found that need an account manager from a quote.");
    } catch (e) {
      toast.error("Scan failed", { description: e.response?.data?.detail || e.message });
    } finally { setScanning(false); }
  };

  const amFor = (r) => overrides[r.person_id] || r.suggested_am;
  const toggle = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSel = rows && rows.length > 0 && rows.every((r) => selected.has(r.person_id));
  const toggleAll = () => setSelected(allSel ? new Set() : new Set((rows || []).map((r) => r.person_id)));

  const apply = async () => {
    const items = (rows || []).filter((r) => selected.has(r.person_id)).map((r) => ({ person_id: r.person_id, am: amFor(r) })).filter((x) => x.am);
    if (!items.length) { toast.error("Select at least one customer"); return; }
    setApplying(true);
    try {
      const { data } = await api.post("/crm/am-sync/apply", { items, source: "quote sync" });
      toast.success(`Applied to ${data.applied} customer${data.applied === 1 ? "" : "s"}`);
      // drop applied rows from the list
      setRows((prev) => (prev || []).filter((r) => !selected.has(r.person_id)));
      setSelected(new Set());
    } catch (e) {
      toast.error("Apply failed", { description: e.response?.data?.detail || e.message });
    } finally { setApplying(false); }
  };

  return (
    <Layout>
      <div className="space-y-5 pb-16 max-w-5xl" data-testid="am-sync-page">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-mono mb-1 flex items-center gap-1.5">
            <UserCog className="w-3 h-3" /> Config · Account Managers
          </div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Account Manager Sync</h1>
          <p className="text-sm text-slate-400 mt-1.5 max-w-2xl">
            Find customers with <span className="text-white">no account manager</span> who already had one on a quote, and copy it across.
            It sets the account manager on the customer record — so it shows on the deal, the 360 and their quotes at once.
            <span className="text-amber-300"> Nothing changes until you press Apply.</span>
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-end gap-2 p-3.5 rounded-xl border border-white/[0.08] bg-white/[0.02]">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1.5">Tier (optional)</label>
            <div className="relative">
              <select value={tier} onChange={(e) => setTier(e.target.value)} data-testid="am-sync-tier"
                className="appearance-none pl-3 pr-8 py-2 rounded-lg bg-white/[0.03] border border-white/10 hover:border-white/20 outline-none text-sm text-slate-200 cursor-pointer">
                <option value="">All tiers</option>
                {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-slate-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1.5">Max rows</label>
            <input type="number" min={1} max={200} value={limit} onChange={(e) => setLimit(Math.max(1, Math.min(200, Number(e.target.value) || 50)))}
              data-testid="am-sync-limit" className="w-24 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 outline-none text-sm text-white" />
          </div>
          <button type="button" onClick={scan} disabled={scanning} data-testid="am-sync-scan"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-500/90 hover:bg-amber-400 text-slate-900 font-semibold text-sm disabled:opacity-50">
            {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Scan
          </button>
          {rows && (
            <div className="ml-auto flex items-center gap-3">
              <span className="text-[12px] text-slate-400 font-mono" data-testid="am-sync-result-count">{rows.length} found · {selected.size} selected</span>
              <button type="button" onClick={apply} disabled={applying || selected.size === 0} data-testid="am-sync-apply"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-sm disabled:opacity-40">
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Apply {selected.size || ""}
              </button>
            </div>
          )}
        </div>

        {/* Results */}
        {rows && rows.length > 0 && (
          <div className="rounded-xl border border-white/[0.08] overflow-hidden" data-testid="am-sync-results">
            <div className="grid grid-cols-[36px_minmax(180px,1.4fr)_120px_1fr_90px_120px] gap-3 px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-white/5 bg-white/[0.02] items-center">
              <label className="grid place-items-center cursor-pointer"><input type="checkbox" checked={allSel} onChange={toggleAll} data-testid="am-sync-select-all" className="w-4 h-4 rounded border-white/20 bg-white/5 accent-amber-500" /></label>
              <div>Customer</div><div>Tier · LTV</div><div>Account manager (from quote)</div><div>Quote</div><div>From</div>
            </div>
            {rows.map((r) => (
              <div key={r.person_id} data-testid={`am-sync-row-${r.person_id}`}
                className={`grid grid-cols-[36px_minmax(180px,1.4fr)_120px_1fr_90px_120px] gap-3 px-4 py-2.5 border-b border-white/[0.04] items-center text-sm ${selected.has(r.person_id) ? "bg-amber-500/[0.06]" : "hover:bg-white/[0.02]"}`}>
                <label className="grid place-items-center cursor-pointer"><input type="checkbox" checked={selected.has(r.person_id)} onChange={() => toggle(r.person_id)} data-testid={`am-sync-select-${r.person_id}`} className="w-4 h-4 rounded border-white/20 bg-white/5 accent-amber-500" /></label>
                <div className="min-w-0"><div className="text-white truncate">{r.full_name || "—"}</div><div className="text-[11px] text-slate-500 truncate">{r.company || ""}</div></div>
                <div className="text-[11px]"><span className="font-mono text-slate-300">{r.tier || "—"}</span><div className="text-emerald-300/80 font-mono">{money(r.ltv)}</div></div>
                <div className="relative">
                  <select value={amFor(r)} onChange={(e) => setOverrides((o) => ({ ...o, [r.person_id]: e.target.value }))} data-testid={`am-sync-am-${r.person_id}`}
                    className="appearance-none w-full pl-7 pr-7 py-1.5 rounded-md bg-white/[0.04] border border-white/12 hover:border-amber-500/40 outline-none text-[12.5px] text-amber-200 cursor-pointer">
                    {!amNames.includes(amFor(r)) && amFor(r) && <option value={amFor(r)}>{amFor(r)}</option>}
                    {amNames.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <Crown className="w-3 h-3 text-amber-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <ChevronDown className="w-3 h-3 text-slate-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                <div className="text-[11px] font-mono text-slate-400 truncate">{r.quote_ref || "—"}{r.quote_count > 1 ? ` (+${r.quote_count - 1})` : ""}</div>
                <div className="text-[11px] font-mono text-slate-400">{fmtDate(r.quote_date)}</div>
              </div>
            ))}
          </div>
        )}
        {rows && rows.length === 0 && (
          <div className="p-12 text-center rounded-xl border border-white/[0.06] text-slate-500 text-sm" data-testid="am-sync-empty">
            Nothing to sync — every scanned customer either already has an account manager or has no quote with one.
          </div>
        )}

        {/* AI teaser — Phase 2 (built next) */}
        <div className="p-4 rounded-xl border border-purple-500/25 bg-purple-500/[0.05] flex items-start gap-3" data-testid="am-sync-ai-teaser">
          <Sparkles className="w-5 h-5 text-purple-300 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-purple-100">AI-assisted account manager suggestions <span className="text-[10px] font-mono uppercase tracking-widest text-purple-300/80 ml-1">coming next</span></div>
            <div className="text-[12.5px] text-slate-400 mt-1 max-w-2xl">
              Next phase: pick up to 25 customers, choose an AI engine, and let it read their calls, emails (incl. who signed off), activities, quotes and order/staff notes to summarise the relationship and <span className="text-slate-200">suggest</span> the best account manager with full justification. Human-confirmed — nothing applies without your review.
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
